var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { action, observable, runInAction, computed, makeObservable, reaction } from "mobx";
import { API_PATH, CHAT_STATUS, FILTER_AGENTS, FILTER_DEPARTMENTS, LOCAL_STORAGE_KEYS } from "../../constants";
import { getAxios } from "../../backend";
import { CHAT_STATUSES_NAMES, Routes, EventName, User, ChatAccessType, } from "@whatsper/texterchat-common";
import { objectFill, strEnum, updateLocalStorage } from "../../helpers";
import { findIndex, throttle, isEqual } from "lodash";
import { LifecycleEvent } from "../../_events";
export const CHATS_GROUP = strEnum(["ALL", ...CHAT_STATUSES_NAMES]);
export const USER_UNREAD_CHATS_STORAGE_KEY = 'userUnreadChats';
const loadChatsThrottle = 1000;
const loadCountThrottle = 1000;
const refreshThrottle = 1000;
/** Reload chats list and count interval in case if event stream not open */
const pollingIntervalMsec = 10000;
/** Count of chats to load at once */
const chatsPageItems = 50;
/** Static query params specific to groups */
const chatsGroupLoadQuery = {
    PENDING: {
        ownFirst: true,
    },
    ASSIGNED: {
        ownFirst: true,
    },
    BULK: {
        ownBulkFirst: true,
    },
};
class ChatsStore {
    constructor(rootStore) {
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Cancel previous load requests when this needed */
        Object.defineProperty(this, "chatsLoadAbortController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "count", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "countLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentGroup", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: CHATS_GROUP.PENDING
        });
        Object.defineProperty(this, "chats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.array()
        });
        Object.defineProperty(this, "favoriteChats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.array()
        });
        Object.defineProperty(this, "chatsLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ChatsLoading.NONE
        });
        Object.defineProperty(this, "totalCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "searchFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                text: null,
                agent: localStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_AGENTS_FILTER),
                department: localStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_DEPARTMENTS_FILTER),
                channel: null,
                labels: null,
            }
        });
        Object.defineProperty(this, "filtersJson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        /** Handle of polling interval, launched in case of events stream fail */
        Object.defineProperty(this, "pollingInterval", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** Last time chats was refreshed (timestamp) */
        Object.defineProperty(this, "lastRefreshedTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        /**
         * Stop async routines.
         * Method should be called before object being destroyed or store setup again
         */
        Object.defineProperty(this, "destroy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.stopPolling();
                if (null !== this.chatsLoadAbortController) {
                    this.chatsLoadAbortController.abort();
                    this.chatsLoadAbortController = null;
                }
            }
        });
        Object.defineProperty(this, "loadInitial", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                yield Promise.all([this.loadCount(), this.load(true)]);
            })
        });
        Object.defineProperty(this, "refresh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: throttle((silent = true) => {
                this.loadCount(silent);
                this.load(false, silent);
            }, refreshThrottle)
        });
        /**
         * Load chats count
         *
         * @param silent Set to TRUE to not show loading
         */
        Object.defineProperty(this, "loadCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (silent = false) {
                try {
                    if (!silent) {
                        runInAction(() => (this.countLoading = true));
                    }
                    yield this.fetchCount().then((count) => {
                        runInAction(() => {
                            this.count = count;
                            this.countLoading = false;
                        });
                    });
                }
                catch (error) {
                    console.error("Error on chats count loading", error);
                    runInAction(() => (this.countLoading = false));
                }
            })
        });
        Object.defineProperty(this, "loadCountThrottled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: throttle(this.loadCount, loadCountThrottle, { trailing: true })
        });
        Object.defineProperty(this, "fetchCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (params = {}) {
                if (this.searchFilter.departmentConditionFilter) {
                    params.departmentConditionFilter = this.searchFilter.departmentConditionFilter;
                }
                return yield getAxios()
                    .then((axios) => {
                    return axios.get(`${API_PATH.CHATS}/count`, { params });
                })
                    .then(({ data }) => {
                    return data;
                });
            })
        });
        Object.defineProperty(this, "load", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (resetPagination = false, silent = false) {
                return this._load(false, resetPagination, silent);
            })
        });
        Object.defineProperty(this, "loadThrottled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: throttle(this.load, loadChatsThrottle, { trailing: true })
        });
        Object.defineProperty(this, "reloadServerSideFilteredChatsThrottled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.hasServerSideFilterApplied) {
                    return false;
                }
                setTimeout(() => {
                    this.loadThrottled(true, true);
                }, 500);
                return true;
            }
        });
        Object.defineProperty(this, "loadMore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (silent = false) {
                return this._load(true, false, silent);
            })
        });
        Object.defineProperty(this, "loadMoreThrottled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: throttle(this.loadMore, loadChatsThrottle, { trailing: true })
        });
        Object.defineProperty(this, "_load", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (more = false, resetPagination = false, silent = false) {
                try {
                    const textSearch = !!this.searchFilter.text;
                    let params = Object.assign({}, chatsGroupLoadQuery[this.currentGroup]);
                    if (more) {
                        if (this.totalCount !== null && this.chats.length >= this.totalCount) {
                            console.warn("Skipped chats loading more because all chats in group loaded already");
                            return;
                        }
                        params.skip = this.chats.length;
                        params.limit = chatsPageItems;
                    }
                    else {
                        params.limit = resetPagination || !this.chats.length ? chatsPageItems : this.chats.length;
                    }
                    if (this.currentGroup !== CHATS_GROUP.ALL && !textSearch) {
                        params.status = this.currentGroup;
                    }
                    if (this.searchFilter.text) {
                        params.search = this.searchFilter.text;
                    }
                    if (this.searchFilter.agent) {
                        params.agent = this.searchFilter.agent;
                    }
                    if (this.searchFilter.department) {
                        params.department = this.searchFilter.department;
                    }
                    if (this.searchFilter.labels && this.searchFilter.labels.length > 0) {
                        params.labels = this.searchFilter.labels;
                    }
                    if (this.searchFilter.channel) {
                        params.channelName = this.searchFilter.channel.name;
                        params.channelAccountId = this.searchFilter.channel.id;
                    }
                    if (this.searchFilter.departmentConditionFilter) {
                        params.departmentConditionFilter = this.searchFilter.departmentConditionFilter;
                    }
                    const filtersJson = JSON.stringify(this.searchFilter);
                    // Cancel previous request
                    if (null !== this.chatsLoadAbortController) {
                        this.chatsLoadAbortController.abort();
                        this.chatsLoadAbortController = null;
                    }
                    if (!silent) {
                        runInAction(() => {
                            this.chatsLoading = more ? ChatsLoading.MORE : ChatsLoading.ALL;
                        });
                    }
                    // Unfortunatelly we can't reuse instance each time,
                    // and instead need new instance every time,
                    // because otherwise it cancel request made after .abort() called
                    this.chatsLoadAbortController = new AbortController();
                    yield getAxios(false)
                        .then((axios) => {
                        var _a;
                        return axios.get(API_PATH.CHATS, {
                            params,
                            signal: (_a = this.chatsLoadAbortController) === null || _a === void 0 ? void 0 : _a.signal,
                        });
                    })
                        .then(({ data: result }) => {
                        runInAction(() => {
                            if (more) {
                                this.chats.push(...result.chats);
                            }
                            else {
                                this.chats.replace(result.chats);
                            }
                            this.totalCount = result.totalFound;
                            this.filtersJson = filtersJson;
                            this.lastRefreshedTime = Date.now();
                            this.chatsLoading = ChatsLoading.NONE;
                            if (textSearch) {
                                this._extendSearchToAllGroups(this.chats);
                            }
                        });
                    });
                }
                catch (error) {
                    // Do not log as an error, because this is expected behaviour.
                    // Also, do not change "loading" property, since request canceled
                    // only if new started
                    if (error.__CANCEL__) {
                        console.debug("Chats load canceled");
                    }
                    else {
                        console.error("Error on chats loading", error);
                        if (!silent) {
                            runInAction(() => (this.chatsLoading = ChatsLoading.NONE));
                        }
                    }
                }
            })
        });
        Object.defineProperty(this, "fetchChats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (params) => __awaiter(this, void 0, void 0, function* () {
                return yield getAxios()
                    .then((axios) => {
                    return axios.get(API_PATH.CHATS, { params });
                })
                    .then(({ data }) => {
                    return data;
                });
            })
        });
        Object.defineProperty(this, "fetchMultipleChatsByIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatIds) => __awaiter(this, void 0, void 0, function* () {
                return yield getAxios()
                    .then((axios) => {
                    return axios.get(`${API_PATH.CHATS}/multiple`, {
                        params: { ids: chatIds }
                    });
                })
                    .then(({ data }) => {
                    return data;
                });
            })
        });
        Object.defineProperty(this, "fetchChatById", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId) => __awaiter(this, void 0, void 0, function* () {
                return this.fetchChat(chatId, false);
            })
        });
        /**
         * Get chat by phone number. Returns chat is found, null otherwise
         *
         * @deprecated
         *
         * @param phone
         */
        Object.defineProperty(this, "fetchChatByPhone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (phone) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const chat = yield this.fetchChat(phone, true);
                    return chat;
                }
                catch (error) {
                    if (error.response && error.response.status === 404) {
                        return null;
                    }
                    throw error;
                }
            })
        });
        Object.defineProperty(this, "assignChat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, agentUid, departmentId) => __awaiter(this, void 0, void 0, function* () {
                const request = {
                    agentUid,
                    departmentId,
                };
                return getAxios()
                    .then((axios) => {
                    return axios.post(`${API_PATH.CHATS}/${chatId}/assign`, request);
                })
                    .then(({ data }) => {
                    this.rootStore.events.emit(EventName.chat_updated, data);
                    this.rootStore.activeChatStore.loadChatAccess(data);
                    return data;
                });
            })
        });
        Object.defineProperty(this, "patchChat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, update) => __awaiter(this, void 0, void 0, function* () {
                return getAxios()
                    .then((axios) => {
                    return axios.patch(`${API_PATH.CHATS}/${chatId}`, update);
                })
                    .then(({ data }) => {
                    this.rootStore.events.emit(EventName.chat_updated, data);
                    return data;
                });
            })
        });
        Object.defineProperty(this, "loadFavoriteChats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const { favorites } = this.rootStore.loginStore.userDetails || {};
                if (!favorites || favorites.length === 0)
                    return;
                const favoriteChatIds = (_a = favorites === null || favorites === void 0 ? void 0 : favorites.filter((chat) => chat.type === User.Favorites.Type.chat).map((chat) => chat.id)) !== null && _a !== void 0 ? _a : [];
                const chats = yield this.fetchMultipleChatsByIds(favoriteChatIds);
                this.favoriteChats.replace(chats);
            })
        });
        Object.defineProperty(this, "subscribeUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, subscribe) => __awaiter(this, void 0, void 0, function* () {
                return getAxios()
                    .then((axios) => {
                    return axios.post(`${API_PATH.CHATS}/${chatId}/subscribe`, { subscribe });
                })
                    .then(() => {
                    this.rootStore.activeChatStore.refresh();
                })
                    .catch((error) => {
                    console.error("Error on chat subscribe", error);
                });
            })
        });
        Object.defineProperty(this, "getUnsubscribedChats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (request) => __awaiter(this, void 0, void 0, function* () {
                const { filterPhoneNumbers } = request, baseRequest = __rest(request, ["filterPhoneNumbers"]);
                const chunks = [];
                if (!filterPhoneNumbers || filterPhoneNumbers.length === 0) {
                    chunks.push([]);
                }
                else {
                    for (let i = 0; i < filterPhoneNumbers.length; i += 1000) {
                        chunks.push(filterPhoneNumbers.slice(i, i + 1000));
                    }
                }
                try {
                    const axios = yield getAxios();
                    const allResults = [];
                    for (let i = 0; i < chunks.length; i++) {
                        const chunkRequest = Object.assign(Object.assign({}, baseRequest), { filterPhoneNumbers: chunks[i].length > 0 ? chunks[i] : undefined, skip: (baseRequest.skip || 0) + (i * 1000) });
                        const { data } = yield axios.post(`${API_PATH.CHATS}/unsubscribed`, chunkRequest);
                        if (data === null || data === void 0 ? void 0 : data.chats) {
                            allResults.push(...data.chats);
                        }
                        if (i < chunks.length - 1) {
                            yield new Promise(resolve => setTimeout(resolve, 500));
                        }
                    }
                    return {
                        chats: allResults,
                        totalFound: allResults.length
                    };
                }
                catch (error) {
                    console.error("Error on fetching unsubscribed chats", error);
                    throw error;
                }
            })
        });
        Object.defineProperty(this, "setSearchQuery", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (search) => {
                const changed = this.searchFilter.text !== search;
                this.searchFilter.text = search;
                if (changed) {
                    this.loadThrottled(true);
                }
            }
        });
        Object.defineProperty(this, "setDepartmentFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (department) => {
                if (department === FILTER_DEPARTMENTS.ALL_DEPARTMENTS) {
                    department = null;
                }
                let changed = department !== this.searchFilter.department;
                this.searchFilter.department = department;
                updateLocalStorage(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_DEPARTMENTS_FILTER, department);
                if (changed) {
                    // Unlike text, this and agent filters have no intermediate value
                    // so load can be loaded without throttle
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "setAgentFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (agent) => {
                if (agent === FILTER_AGENTS.ALL_AGENTS) {
                    agent = null;
                }
                let changed = agent !== this.searchFilter.agent;
                this.searchFilter.agent = agent;
                updateLocalStorage(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_AGENTS_FILTER, agent);
                if (changed) {
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "setLabelsFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (labels) => {
                const changed = JSON.stringify(this.searchFilter.labels) !== JSON.stringify(labels);
                this.searchFilter.labels = labels;
                if (changed) {
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "resetFilters", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { text, agent, department, channel, labels } = this.searchFilter;
                const changed = text || agent || department || channel || (labels && labels.length > 0);
                this.searchFilter.text = null;
                this.searchFilter.agent = null;
                this.searchFilter.department = null;
                this.searchFilter.channel = null;
                this.searchFilter.labels = null;
                localStorage.removeItem(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_AGENTS_FILTER);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.FILTERS.CHATS_LIST_DEPARTMENTS_FILTER);
                if (changed) {
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "setDepartmentConditionFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (departmentConditionFilter) => {
                let changed = departmentConditionFilter !== this.searchFilter.departmentConditionFilter;
                if (departmentConditionFilter) {
                    this.searchFilter.departmentConditionFilter = departmentConditionFilter;
                }
                else {
                    delete this.searchFilter.departmentConditionFilter;
                }
                if (changed) {
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "getMediaFilesList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_a) => __awaiter(this, [_a], void 0, function* ({ chatId, mediaTypes, }) {
                const params = { mediaTypes };
                return getAxios()
                    .then((axios) => {
                    return axios.get(`${API_PATH.CHATS}/media/${chatId}/list`, { params });
                })
                    .then(({ data }) => {
                    return data;
                })
                    .catch((error) => {
                    console.error('Error while fetching media files media list', error);
                });
            })
        });
        Object.defineProperty(this, "toggleMarkFavorite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, important) => __awaiter(this, void 0, void 0, function* () {
                if (important) {
                    yield this.rootStore.agentsStore.markFavorite([{
                            type: User.Favorites.Type.chat,
                            id: chatId
                        }]);
                }
                else {
                    yield this.rootStore.agentsStore.unmarkFavorite(chatId, User.Favorites.Type.chat);
                }
            })
        });
        Object.defineProperty(this, "isChatImportant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId) => {
                var _a;
                const favorites = ((_a = this.rootStore.loginStore.userDetails) === null || _a === void 0 ? void 0 : _a.favorites) || [];
                return favorites
                    .filter((favorite) => favorite.type === User.Favorites.Type.chat)
                    .some((favorite) => favorite.id === chatId);
            }
        });
        Object.defineProperty(this, "updateChatLabels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, labelsPayload) => __awaiter(this, void 0, void 0, function* () {
                let chat = this.chats.find(c => c._id === chatId) || this.favoriteChats.find(c => c._id === chatId);
                if (!chat) {
                    // In case we are calling this outside the chat list for some future reason
                    chat = yield this.fetchChatById(chatId);
                }
                if (!chat) {
                    throw new Error(`Chat with ID ${chatId} not found.`);
                }
                const hasManageAccess = yield this.rootStore.agentsStore.getAgentChatAccess(chat, ChatAccessType.manage);
                if (!hasManageAccess) {
                    console.error(`Agent does not have manage access for chat ${chatId}.`);
                    throw new Error(`Agent does not have manage access for chat ${chatId}.`);
                }
                return getAxios()
                    .then((axios) => {
                    return axios.post(`${API_PATH.CHATS}/${chatId}/labels`, labelsPayload);
                })
                    .then(({ data: updatedChatData }) => {
                    const existingChat = this.chats.find(c => c._id === chatId) || this.favoriteChats.find(c => c._id === chatId);
                    if (existingChat) {
                        const mergedChat = Object.assign(Object.assign({}, existingChat), updatedChatData);
                        this.onChatUpdatedEvent(mergedChat);
                        return mergedChat;
                    }
                    this.onChatUpdatedEvent(updatedChatData);
                    return updatedChatData;
                })
                    .catch((error) => {
                    console.error(`Failed to update labels for chat ${chatId}`, error);
                    throw error;
                });
            })
        });
        Object.defineProperty(this, "setChannel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (channel, initSearch = true) => {
                const changed = channel && !this.searchFilter.channel
                    || !channel && this.searchFilter.channel
                    || channel
                        && this.searchFilter.channel
                        && !(channel.name === this.searchFilter.channel.name && channel.id === this.searchFilter.channel.id);
                this.searchFilter.channel = channel;
                if (changed && initSearch) {
                    this.load(true);
                }
            }
        });
        Object.defineProperty(this, "_extendSearchToAllGroups", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chats) => {
                const chatsWithGroup = chats.filter((chat) => CHAT_STATUS[chat.status] === this.currentGroup);
                if (chatsWithGroup.length > 0) {
                    this.chats.replace(chatsWithGroup);
                    this.totalCount = chatsWithGroup.length;
                }
                else {
                    this.setCurrentGroup(CHATS_GROUP.ALL, true);
                }
            }
        });
        Object.defineProperty(this, "isChatUnreadForUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chat) => {
                var _a;
                if (!chat) {
                    return { isUnread: false, showCount: false };
                }
                if (chat.unreadCount > 0) {
                    return { isUnread: true, showCount: true };
                }
                const unreadMap = (_a = this.rootStore.storage.getStorageValue(USER_UNREAD_CHATS_STORAGE_KEY, {})) !== null && _a !== void 0 ? _a : {};
                if (unreadMap[chat._id]) {
                    return { isUnread: true, showCount: false };
                }
                return { isUnread: false, showCount: false };
            }
        });
        Object.defineProperty(this, "markChatUnreadForUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const chat = (_b = (_a = this.chats.find(c => c._id === chatId)) !== null && _a !== void 0 ? _a : this.favoriteChats.find(c => c._id === chatId)) !== null && _b !== void 0 ? _b : null;
                const unreadStatus = this.isChatUnreadForUser(chat);
                if (unreadStatus.isUnread) {
                    console.debug(`Chat ${chatId} is already unread for user, skipping.`);
                    return;
                }
                try {
                    yield this.rootStore.storage.setItemInMap(USER_UNREAD_CHATS_STORAGE_KEY, chatId, true);
                }
                catch (error) {
                    console.error(`Failed to mark chat ${chatId} as unread for user`, error);
                }
            })
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.count = {
            all: 0,
            waiting: 0,
            status: objectFill(CHAT_STATUSES_NAMES, 0),
        };
        this.rootStore.events
            .addListener(EventName.chat_created, (data) => this.onChatCreatedEvent(data))
            .addListener(EventName.chat_updated, (data) => this.onChatUpdatedEvent(data))
            .addListener(EventName.chat_deleted, (data) => this.onChatDeletedEvent(data))
            .addListener(LifecycleEvent.STREAM_OPEN, () => this.stopPolling())
            .addListener(LifecycleEvent.STREAM_FAILED, () => this.startPolling());
        /** Load favorite chats after user logs in and listen for favorites change */
        reaction(() => { var _a, _b; return (_b = (_a = this.rootStore.loginStore) === null || _a === void 0 ? void 0 : _a.userDetails) === null || _b === void 0 ? void 0 : _b.favorites; }, (favorites) => {
            if (favorites) {
                this.loadFavoriteChats();
            }
        });
    }
    get currentGroupStatus() {
        switch (this.currentGroup) {
            case CHATS_GROUP.PENDING:
                return CHAT_STATUS.PENDING;
            case CHATS_GROUP.ASSIGNED:
                return CHAT_STATUS.ASSIGNED;
            case CHATS_GROUP.BULK:
                return CHAT_STATUS.BULK;
            case CHATS_GROUP.RESOLVED:
                return CHAT_STATUS.RESOLVED;
            case CHATS_GROUP.BOT:
                return CHAT_STATUS.BOT;
            case CHATS_GROUP.ALL:
                return undefined;
            default:
                return undefined;
        }
    }
    get chatsHasMore() {
        return this.totalCount ? this.totalCount > this.chats.length : false;
    }
    get channel() {
        return this.searchFilter.channel;
    }
    get hasFilterApplied() {
        return !!(this.searchFilter.text || this.searchFilter.agent || this.searchFilter.department || this.searchFilter.channel || (this.searchFilter.labels && this.searchFilter.labels.length > 0));
    }
    get hasServerSideFilterApplied() {
        return !!(this.searchFilter.text || (this.searchFilter.labels && this.searchFilter.labels.length > 0));
    }
    setCurrentGroup(groupName, keepSearchQuery = false) {
        const changed = groupName !== this.currentGroup;
        this.currentGroup = groupName;
        if (changed && !keepSearchQuery) {
            this.setSearchQuery("");
        }
        if (changed || typeof this.totalCount !== "number") {
            this.load(true);
        }
        else {
            const filtersJson = JSON.stringify(this.searchFilter);
            if (filtersJson !== this.filtersJson) {
                this.load(true);
            }
        }
    }
    /**
     * Get chat by channel information. Returns chat if it found, NULL otherwise
     *
     * @param param0 Channel info
     */
    fetchChatByChannelId(_a) {
        return __awaiter(this, arguments, void 0, function* ({ name, accountId, id }) {
            try {
                const chat = yield getAxios(false)
                    .then((axios) => {
                    return axios.get(`${API_PATH.CHATS}/channel/${name}/${accountId}/${id}`);
                })
                    .then(({ data }) => {
                    return data;
                });
                return chat;
            }
            catch (error) {
                if (error.response && error.response.status === 404) {
                    return null;
                }
                throw error;
            }
        });
    }
    fetchChat(criteria, isPhone) {
        return __awaiter(this, void 0, void 0, function* () {
            return getAxios(!isPhone)
                .then((axios) => {
                return axios.get(isPhone ? `${API_PATH.CHATS}/phone/${criteria.replace(/[^\d]/g, "")}` : `${API_PATH.CHATS}/${criteria}`);
            })
                .then(({ data }) => {
                return data;
            });
        });
    }
    resolveChat(chatId, notifyClient, runBot, nodeName) {
        return __awaiter(this, void 0, void 0, function* () {
            let request = {
                notifyClient,
            };
            if (runBot) {
                request = Object.assign(Object.assign({}, request), { runBot: {
                        nodeName,
                    } });
            }
            yield getAxios().then((axios) => {
                return axios.post(`${API_PATH.CHATS}/${chatId}/resolve`, request);
            });
        });
    }
    /**
     * Create new chat
     *
     * @param channelContactId Provider/channel ID of chat contact
     * @param channel Messaging channel. If not provided used default one
     * @param accountId Messaging channel account ID. If not provided used default one
     *
     * @throws Error If channel infoo not provided and found no default channel
     */
    createChatByChannelId(channelContactId, channel, accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultAccount = this.rootStore.channels.defaultAccount;
            if (!defaultAccount) {
                throw new Error('No default channel account found!');
            }
            const request = {
                channel: channel || defaultAccount.channel,
                accountId: accountId || defaultAccount.accountId,
                channelContactId,
            };
            return getAxios()
                .then((axios) => {
                return axios.post(API_PATH.CHATS, request);
            })
                .then(({ data }) => {
                return data;
            });
        });
    }
    bulkResolve(statusToClear, hours) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                beforeTimestamp: Date.now() - hours * (60 * 60 * 1000),
                status: statusToClear,
            };
            return getAxios().then((axios) => {
                return axios.post(`${API_PATH.CHATS}/bulk-resolve`, params);
            });
        });
    }
    /**
     * Set read specified IDs or all messages of the chat
     *
     * @param chatId Parent chat ID
     * @param messages Messages IDs or boolean TRUE to set read all chat messages
     * @param idempotencyKey Value to send as `Idempotency-Key` header to avoid processing same request more than once
     */
    setMessagesRead(chatId, messages, idempotencyKey) {
        return __awaiter(this, void 0, void 0, function* () {
            let params;
            if (Array.isArray(messages)) {
                params = { messages };
            }
            else {
                params = { all: true };
            }
            return getAxios()
                .then((axios) => {
                return axios.post(`${API_PATH.CHATS}/${chatId}/set-read`, params, Object.assign({}, idempotencyKey
                    ? {
                        headers: {
                            'Idempotency-Key': idempotencyKey,
                        }
                    }
                    : {}));
            })
                .then((response) => {
                runInAction(() => {
                    const chat = this.chats.find((c) => c._id === chatId);
                    if (chat) {
                        chat.unreadCount = 0;
                    }
                    const favChat = this.favoriteChats.find((c) => c._id === chatId);
                    if (favChat) {
                        favChat.unreadCount = 0;
                    }
                });
                return response;
            })
                .catch((error) => {
                console.error(`Failed API call to set messages read for chat ${chatId}`, error);
                throw error;
            });
        });
    }
    /** @todo Verify it working */
    getBotSummaryData(id) {
        return __awaiter(this, void 0, void 0, function* () {
            return getAxios()
                .then((axios) => {
                return axios.get(`${API_PATH.BOTS}/session/${id}/summary`);
            })
                .then(({ data }) => {
                return data;
            });
        });
    }
    onChatCreatedEvent(chat) {
        this.onEventReceived();
        this.loadCountThrottled(true);
        if (this.reloadServerSideFilteredChatsThrottled()) {
            return;
        }
        if (this.isChatMatchesFilters(chat)) {
            this.upsertChat(chat);
            // Increase total count of chats
            if (typeof this.totalCount === "number") {
                this.totalCount += 1;
            }
        }
    }
    onChatUpdatedEvent(chatNew) {
        this.onEventReceived();
        setTimeout(() => {
            this.loadCountThrottled(true);
            // Load count with delay to have time for server-side cache to be cleared on chat update
        }, 500);
        if (this.reloadServerSideFilteredChatsThrottled()) {
            this.upsertFavoriteChat(chatNew);
            return;
        }
        // Is chat already present in current loaded list
        const chatPrev = this.chats.find(({ _id }) => _id === chatNew._id), matchesFilters = this.isChatMatchesFilters(chatNew);
        if (!matchesFilters) {
            if (chatPrev && !this.hasServerSideFilterApplied) {
                runInAction(() => {
                    this.chats.replace(this.chats.filter((chat) => {
                        return chat._id !== chatNew._id;
                    }));
                });
            }
        }
        else {
            this.upsertChat(chatNew);
        }
        // sync favorites on any content change
        this.upsertFavoriteChat(chatNew);
    }
    isChatMatchesFilters(chat) {
        if (this.hasServerSideFilterApplied) {
            return false;
        }
        if (typeof this.currentGroupStatus !== 'undefined') {
            if (chat.status !== this.currentGroupStatus) {
                return false;
            }
        }
        // if there is an active filter, check if we should upsert this chat or not
        const { agent, department, channel, departmentConditionFilter } = this.searchFilter;
        // First check if user should see this chat based on their role and department condition filter
        const { userDetails } = this.rootStore.loginStore;
        if (userDetails && userDetails._id !== chat.agent.uid) {
            if (departmentConditionFilter) {
                const userDepartmentsIds = this.rootStore.agentsStore.getUserDepartmentsIds(userDetails._id);
                if (departmentConditionFilter === Routes.Chats.DepartmentConditionFilter.only_own) {
                    if (!chat.departmentId || !userDepartmentsIds.includes(chat.departmentId)) {
                        return false;
                    }
                }
                else if (departmentConditionFilter === Routes.Chats.DepartmentConditionFilter.own_and_unassigned) {
                    if (chat.departmentId && !userDepartmentsIds.includes(chat.departmentId)) {
                        return false;
                    }
                }
            }
        }
        if (agent) {
            if (agent === FILTER_AGENTS.WITH_BOT) {
                // "Not assigned" / "With bot" - server matches an empty agent uid
                // (NO_AGENT_FILTER = { $in: ['', null] }), so match unassigned chats here too.
                if (chat.agent.uid) {
                    return false;
                }
            }
            else if (chat.agent.uid !== agent) {
                return false;
            }
        }
        if (department && chat.departmentId !== department) {
            return false;
        }
        if (channel && chat.channelInfo.accountId !== channel.id) {
            return false;
        }
        return true;
    }
    /**
     * Insert new/updated chat considering special sorting cases
     *
     * @param chat Chat to insert/update
     * @param isNew Is new chat
     */
    upsertChat(chat) {
        // We determine if this is a new chat to the filtered list, by looking at the chats list
        const chats = this.chats.filter(({ _id }) => {
            return _id !== chat._id;
        });
        const prevChat = this.chats.find(({ _id }) => _id === chat._id);
        // If there is a new message, re-sort
        const shouldUpdatePosition = prevChat ? prevChat.last_message_timestamp < chat.last_message_timestamp : true;
        let position = chats.length; // Default to end of list
        if (!shouldUpdatePosition) {
            // No need to sort, chat already have its own position
            position = findIndex(this.chats, ({ _id }) => _id === chat._id);
            if (position < 0)
                position = chats.length;
        }
        else {
            const query = chatsGroupLoadQuery[this.currentGroup] || {}, isOwnFirst = query.ownFirst === true, isOwnBulkFirst = query.ownBulkFirst === true, { _id } = this.rootStore.loginStore.userDetails || {}, own = chat.agent.uid === _id, ownBulk = chat.sendingBulkAgent === _id;
            for (let i = 0; i < chats.length; i++) {
                // It can be single if() but this way it more readable
                // Skip own chats when placing non-own chat (own chats stay at top)
                if (isOwnFirst && !own && chats[i].agent.uid === _id) {
                    continue;
                }
                if (isOwnBulkFirst && !ownBulk && chats[i].sendingBulkAgent === _id) {
                    continue;
                }
                // Insert before first chat with older timestamp (descending order)
                if (chat.last_message_timestamp >= chats[i].last_message_timestamp) {
                    position = i;
                    break;
                }
            }
        }
        this.chats.replace([...chats.slice(0, position), chat, ...chats.slice(position)]);
    }
    /**
     * Sync an existing favorite chat by index, and keep its position
     */
    upsertFavoriteChat(chat) {
        const favoriteIndex = this.favoriteChats.findIndex(({ _id }) => _id === chat._id);
        if (favoriteIndex < 0 || isEqual(this.favoriteChats[favoriteIndex], chat)) {
            return;
        }
        this.favoriteChats.replace([
            ...this.favoriteChats.slice(0, favoriteIndex),
            chat,
            ...this.favoriteChats.slice(favoriteIndex + 1),
        ]);
    }
    onChatDeletedEvent({ _id }) {
        this.onEventReceived();
        this.loadCountThrottled(true);
        runInAction(() => {
            let found = false;
            this.chats.replace(this.chats.filter((chat) => {
                if (_id === chat._id) {
                    found = true;
                    return false;
                }
                return true;
            }));
            if (found && typeof this.totalCount === "number" && this.totalCount > 0) {
                this.totalCount--;
            }
        });
    }
    onEventReceived() {
        this.lastRefreshedTime = Date.now();
        this.stopPolling();
    }
    startPolling() {
        if (null === this.pollingInterval) {
            // Run at begin too
            this.refresh();
            this.pollingInterval = setInterval(() => {
                if (Date.now() - this.lastRefreshedTime >= pollingIntervalMsec) {
                    this.refresh();
                }
            }, pollingIntervalMsec);
        }
    }
    stopPolling() {
        if (null !== this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            // Make sure all pulled for period between latest polling refresh and polling stopped
            this.refresh();
        }
    }
}
__decorate([
    observable
], ChatsStore.prototype, "count", void 0);
__decorate([
    observable
], ChatsStore.prototype, "countLoading", void 0);
__decorate([
    observable
], ChatsStore.prototype, "currentGroup", void 0);
__decorate([
    observable
], ChatsStore.prototype, "chatsLoading", void 0);
__decorate([
    observable
], ChatsStore.prototype, "totalCount", void 0);
__decorate([
    observable
], ChatsStore.prototype, "searchFilter", void 0);
__decorate([
    computed
], ChatsStore.prototype, "currentGroupStatus", null);
__decorate([
    computed
], ChatsStore.prototype, "chatsHasMore", null);
__decorate([
    computed
], ChatsStore.prototype, "channel", null);
__decorate([
    computed
], ChatsStore.prototype, "hasFilterApplied", null);
__decorate([
    computed
], ChatsStore.prototype, "hasServerSideFilterApplied", null);
__decorate([
    action
], ChatsStore.prototype, "setCurrentGroup", null);
__decorate([
    action
], ChatsStore.prototype, "assignChat", void 0);
__decorate([
    action
], ChatsStore.prototype, "resolveChat", null);
__decorate([
    action
], ChatsStore.prototype, "patchChat", void 0);
__decorate([
    action
], ChatsStore.prototype, "loadFavoriteChats", void 0);
__decorate([
    action
], ChatsStore.prototype, "subscribeUser", void 0);
__decorate([
    action
], ChatsStore.prototype, "getUnsubscribedChats", void 0);
__decorate([
    action
], ChatsStore.prototype, "setSearchQuery", void 0);
__decorate([
    action
], ChatsStore.prototype, "setDepartmentFilter", void 0);
__decorate([
    action
], ChatsStore.prototype, "setAgentFilter", void 0);
__decorate([
    action
], ChatsStore.prototype, "setLabelsFilter", void 0);
__decorate([
    action
], ChatsStore.prototype, "resetFilters", void 0);
__decorate([
    action
], ChatsStore.prototype, "setDepartmentConditionFilter", void 0);
__decorate([
    action
], ChatsStore.prototype, "getMediaFilesList", void 0);
__decorate([
    action
], ChatsStore.prototype, "bulkResolve", null);
__decorate([
    action
], ChatsStore.prototype, "updateChatLabels", void 0);
__decorate([
    action
], ChatsStore.prototype, "onChatCreatedEvent", null);
__decorate([
    action
], ChatsStore.prototype, "upsertChat", null);
__decorate([
    action
], ChatsStore.prototype, "upsertFavoriteChat", null);
__decorate([
    action
], ChatsStore.prototype, "setChannel", void 0);
__decorate([
    action
], ChatsStore.prototype, "markChatUnreadForUser", void 0);
export var ChatsLoading;
(function (ChatsLoading) {
    ChatsLoading["ALL"] = "ALL";
    ChatsLoading["MORE"] = "MORE";
    ChatsLoading["NONE"] = "NONE";
})(ChatsLoading || (ChatsLoading = {}));
export default ChatsStore;
